import { useTranslation } from 'react-i18next';
import autolistLogo from 'public/static/images/paid_landing/logo.svg';
// assets
import { screen, colors } from 'components/styles/variables';
import { flexBoxFullWidthCSS } from 'components/styles';
import css from 'styled-jsx/css';
import TextSearchList from 'lib/TextSearchList';
import useMakesAndModels from 'lib/hooks/useMakesAndModels';
import { useState } from 'react';
import MobileModal from 'components/PaidLanding/TextSearch/MobileModal';
import LocationInput from 'components/LocationInput';
import TypeSearch from 'components/PaidLanding/TypeSearch';
import TextSearch from 'components/PaidLanding/TextSearch';
import hero from 'public/static/images/home/hero-image.jpg';
import heroMobile from 'public/static/images/home/hero-image-mobile.jpg';
import SearchObject from 'lib/Search';
import { trackSearch } from 'lib/Tracking';
import Geocode from 'types/Geocode';
import Make from 'types/Make';
import legacySearchEvent, { SearchActions } from 'lib/Tracking/HomePage/LegacySearchEvent';
import locationSearchEvent, {
  LocationSearchActions,
} from 'lib/Tracking/HomePage/LocationSearchEvent';
import Model from 'types/Model';
import useEvents from 'lib/hooks/useEvents';
import keywordSearchEvent, { KeywordSearchActions } from 'lib/Tracking/HomePage/KeywordSearchEvent';
import useLockedScreen from 'lib/hooks/useLockedScreen';
import chevronDown from 'public/static/images/chevron-down-white.svg';
import { useScrollPosition } from '@n8tb1t/use-scroll-position';

const paidLocationCSS = css.resolve`
  .paid-location-input {
    color: white;
    font-size: 16px;
  }
`;

function Hero(): JSX.Element {
  const { t } = useTranslation();
  const [modalOpen, setModalOpen] = useState(false);
  const logger = useEvents();

  const openModal = (): void => setModalOpen(true);
  const closeModal = (): void => setModalOpen(false);

  const makesAndModels = useMakesAndModels();

  const textSearchList = new TextSearchList(makesAndModels);

  useLockedScreen(modalOpen);
  const [showArrow, setShowArrow] = useState(true);

  useScrollPosition(
    ({ currPos }) => {
      const isShow = currPos.y === 0;
      if (isShow !== showArrow) setShowArrow(isShow);
    },
    [showArrow]
  );

  return (
    <div className={`hero ${flexBoxFullWidthCSS.className}`}>
      <div className="container">
        {modalOpen && <MobileModal closeModal={closeModal} fullList={textSearchList} />}
        <div className="content-container">
          <div className="autolist-logo" />
          <h1 className="header">{t('paidLanding.title')}</h1>
          <div className="location-input-container">
            <LocationInput
              className={`paid-location-input ${paidLocationCSS.className}`}
              onLocationTap={(): Promise<void> =>
                logger.log(locationSearchEvent({ action: LocationSearchActions.editLocation }))
              }
              onInputTap={(): Promise<void> =>
                logger.log(locationSearchEvent({ action: LocationSearchActions.enterCityField }))
              }
              onCurrentLocationTap={(): Promise<void> =>
                logger.log(locationSearchEvent({ action: LocationSearchActions.currentLocation }))
              }
              onSelectCityTap={(): Promise<void> =>
                logger.log(locationSearchEvent({ action: LocationSearchActions.selectCity }))
              }
            />
          </div>
          <TextSearch
            fullList={textSearchList}
            openModal={openModal}
            closeModal={closeModal}
            isModal={false}
            onSearchInputTap={(): Promise<void> =>
              logger.log(keywordSearchEvent({ action: KeywordSearchActions.searchFieldTap }))
            }
            onSearchFieldSubmit={(): Promise<void> =>
              logger.log(keywordSearchEvent({ action: KeywordSearchActions.searchFieldSubmit }))
            }
          />
          <div className="break">
            <span />
            {t('home.why.or')}
            <span />
          </div>
          <div className="type-search-container">
            <TypeSearch
              onMakeModelSearch={(make?: Make, model?: Model, geolocation?: Geocode): void => {
                const search = new SearchObject({ make, model, location: geolocation });
                trackSearch({ category: 'home_page', params: search.queryParams });
              }}
              onBodyTypeSearch={(bodyType: string, geolocation?: Geocode): void => {
                const search = new SearchObject({ bodyType, location: geolocation });
                trackSearch({ category: 'home_page', params: search.queryParams });
              }}
              onPriceSearch={(priceMin: number, priceMax: number, geolocation?: Geocode): void => {
                const search = new SearchObject({
                  priceMin,
                  priceMax,
                  location: geolocation,
                });
                trackSearch({ category: 'home_page', params: search.queryParams });
              }}
              onMakeModelTap={(): Promise<void> =>
                logger.log(legacySearchEvent({ action: SearchActions.makeModel }))
              }
              onBodyStyleTap={(): Promise<void> =>
                logger.log(legacySearchEvent({ action: SearchActions.bodyStyle }))
              }
              onPriceTap={(): Promise<void> =>
                logger.log(legacySearchEvent({ action: SearchActions.price }))
              }
            />
          </div>
        </div>
      </div>
      {showArrow && (
        <>
          <button
            onClick={(): void =>
              window.scrollTo({ top: window.innerHeight, left: 0, behavior: 'smooth' })
            }
            className="caret"
          />
          <button className="half-circle-arrow"></button>
        </>
      )}
      {flexBoxFullWidthCSS.styles}
      <style jsx>{`
        .hero {
          padding-top: 40px;
          background-size: cover;
          background-position: 50% 0%;
          height: calc(100vh - 63px);
          background-color: black;
          color: white;
          font-weight: 200;
          position: relative;
          margin-bottom: 80px;
          overflow: hidden;
        }

        @media (min-width: 500px) {
          .hero {
            background-image: url(${hero});
          }
        }

        @media (max-width: calc(499px)) {
          .hero {
            background-image: url(${heroMobile});
          }
        }

        @media (min-width: ${screen.smMin}) {
          .hero {
            background-position: 0% 75%;
            height: 528px;
            overflow: inherit;
            margin-bottom: 32px;
          }
        }

        .content-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          width: 100%;
          margin-top: 40px;
        }

        .autolist-logo {
          background-image: url(${autolistLogo});
          background-size: cover;
          width: 190px;
          height: 28px;
        }

        @media (min-width: ${screen.smMin}) {
          .autolist-logo {
            width: 225px;
            height: 32px;
          }
        }

        .header {
          text-align: center;
          padding: 0;
          margin: 20px 0px;
          font-size: 32px;
          font-weight: 700;
          color: white;
        }

        .location-input-container {
          border-bottom: 1px solid white;
          margin-bottom: 28px;
          cursor: pointer;
        }

        .break {
          color: white;
          text-transform: uppercase;
          margin: 20px auto;
          display: flex;
          align-items: center;
          font-weight: bold;
          font-size: 16px;
        }

        .break > span {
          width: 16px;
          border-bottom: 1px solid white;
          margin: 0 5px;
        }

        .type-search-container {
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        @media (min-width: ${screen.smMin}) {
          .header {
            margin: 20px 0;
            font-size: 40px;
          }

          .location-input-container {
            margin-bottom: 37px;
          }

          .break {
            margin: 32px auto;
          }

          .content-container {
            margin-top: 33px;
          }

          .type-search-container {
            display: flex;
            flex-direction: row;
          }
        }

        @media (max-height: 667px) {
          .type-search-container {
            width: 100%;
            flex-direction: row;
          }
        }

        .half-circle-arrow {
          position: fixed;
          bottom: -180px;
          border-radius: 50%;
          opacity: 0.5;
          height: 220px;
          width: 220px;
          margin-left: auto;
          margin-right: auto;
          right: 0;
          left: 0;
          text-align: center;
          background-color: ${colors.BLACK};
          border: none;
        }

        .caret {
          position: fixed;
          border: none;
          bottom: 0;
          margin-left: auto;
          margin-right: auto;
          right: 0;
          left: 0;
          background-color: transparent;
          width: 30px;
          height: 30px;
          background-image: url(${chevronDown});
          background-size: cover;
          z-index: 2;
        }

        @media (min-width: ${screen.smMin}) {
          .caret {
            display: none;
          }

          .half-circle-arrow {
            display: none;
          }
        }
      `}</style>
      {paidLocationCSS.styles}
    </div>
  );
}

export default Hero;
